import { useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import theme from '../../../../../helpers/theme'
import { backendGetRequest } from '../../../../../helpers/callBackend'
import { errorMessage } from '../../../../../helpers/errorMessageFunc'
import { useEffect, useState } from 'react'
import checkFlag from '../../../../../helpers/featureFlags'
import FormattedTabs from '../../../../../components/generic/formattedTabs'
import TransactionCounterpartiesTransactionsTable from './TransactionCounterpartiesTransactionsTable'
import TransactionCounterpartiesListenersTable from './TransactionCounterpartiesListenersTable'
import HeaderBox from '../../../../../components/generic/arpariBoxes/headerBox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import GenericTagChips from '../../../../../components/specific/customTags/genericTagChips'
import AddTagsButton from './controls/AddTagsButton'
import Chip from '@mui/material/Chip'
import EditCounterpartyButton from './controls/EditCounterpartyButton'
import { Link } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { documentTitle } from '../../../../../components/header/documentTitle'

const TransactionCounterpartyDetails = () => {
  const [counterparty, setCounterparty] = useState({})
  const [originalTableData, setOriginalTableData] = useState([])
  const [displayedTableData, setDisplayedTableData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [totalRecords, setTotalRecords] = useState(-1)
  const [cleanedCounterparties, setCleanedCounterparties] = useState([])
  const [displayCleanedCounterparties, setDisplayCleanedCounterparties] = useState([])
  const [cleanedCounterpartiesLoading, setCleanedCounterpartiesLoading] = useState(false)
  const [displayTags, setDisplayTags] = useState([])

  const todayDate = new Date()
  let [currentMonth, setCurrentMonth] = useState(todayDate.getMonth() + 1)
  let [currentYear, setCurrentYear] = useState(todayDate.getFullYear())

  const { id } = useParams()

  const decrementMonth = () => {
    let month = currentMonth
    let year = currentYear
    if (month === 1) {
      month = 12
      year -= 1
    } else {
      month -= 1
    }
    setCurrentMonth(month)
    setCurrentYear(year)
  }

  const getChildCounterparties = async () => {
    try {
      setCleanedCounterpartiesLoading(true)
      const cleanedResp = await backendGetRequest('/api/transactions/counterparties/cleaned', {
        params: {
          transaction_counterparty_id: id,
        },
      })
      setCleanedCounterparties(cleanedResp.data?.data)
      setDisplayCleanedCounterparties(cleanedResp.data?.data)
    } catch (error) {
      errorMessage('Error fetching cleaned counterparty details')
    }
    setCleanedCounterpartiesLoading(false)
  }

  const getCounterpartyDetails = async () => {
    try {
      const cptyResp = await backendGetRequest('/api/transactions/counterparties', {
        params: {
          counterparty_id: id,
        },
      })
      setCounterparty(cptyResp.data?.data)
    } catch (error) {
      errorMessage('Error fetching counterparty details')
    }
  }

  const getTransactionsPage = async (pageNum) => {
    try {
      const transactionsResp = await backendGetRequest('/api/transactions', {
        params: {
          page_index: pageNum,
          counterparty_id: id,
          month: currentMonth,
          year: currentYear,
        },
      })
      const transactionsArray = transactionsResp.data?.data
      setOriginalTableData((prevState) => [...prevState, ...transactionsArray])
    } catch (error) {
      errorMessage('Error fetching transactions')
    }
  }

  const getCounterpartyTransactions = async () => {
    setIsLoading(true)
    let totalPages = 0
    try {
      const pageInfo = await backendGetRequest('/api/transactions', {
        params: {
          page_index: 0,
          counterparty_id: id,
          month: currentMonth,
          year: currentYear,
        },
      })
      totalPages = pageInfo.data?.total_pages
      const totalRecs = pageInfo.data?.total_records
      if (totalRecs === 0 && totalRecords < 0) {
        setTotalRecords(0)
      } else {
        setTotalRecords((prevState) => prevState + pageInfo.data?.total_records)
      }
    } catch {
      return
    }

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      getTransactionsPage(pageNum)
    }
    decrementMonth()
  }

  const onUpdateEntity = (updatedCounterparty) => {
    setCounterparty(updatedCounterparty)
  }

  useEffect(() => {
    if (!checkFlag('transactionsCounterparties')) {
      return
    }
    getCounterpartyDetails()
    getChildCounterparties()
    getCounterpartyTransactions()
  }, [])

  useEffect(() => {
    if (counterparty.name) {
      documentTitle({ text: `Transaction Counterparties | ${counterparty.name}` })
    } else {
      documentTitle({ text: 'Transaction Counterparties' })
    }
  }, [counterparty])

  useEffect(() => {
    if (totalRecords >= 0 && originalTableData.length >= totalRecords) {
      setIsLoading(false)
      setDisplayedTableData(originalTableData)
    }
  }, [originalTableData])

  return (
    <Box sx={{ overflow: 'auto', maxHeight: '99%' }}>
      <Box sx={{ margin: theme.dashboard.margins }}>
        <HeaderBox
          ignoreDocumentTitle
          title={
            <Stack direction='row' alignItems='center'>
              {counterparty.name || 'Counterparty details'}
              <Chip label={counterparty.entity ? 'internal' : 'external'} sx={{ fontSize: 12, marginLeft: '10px' }} />
            </Stack>
          }
          actions={<EditCounterpartyButton transactionCounterparty={counterparty} onSuccessfulEdit={onUpdateEntity} />}
          body={
            <>
              {counterparty.description ? (
                <Stack
                  direction='row'
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {counterparty.description && (
                    <Typography color='secondary' sx={{ fontStyle: 'italic' }}>
                      {counterparty.description || ''}
                    </Typography>
                  )}
                </Stack>
              ) : null}
              <Stack direction='row' spacing={0.5} alignItems='end'>
                <GenericTagChips genericTags={displayTags} />
                <AddTagsButton
                  transactionCounterparty={counterparty}
                  setDisplayTags={setDisplayTags}
                />
              </Stack>
              {counterparty.entity && (
                <Link
                  to={`/holding-entities/details/000${counterparty.entity}`}
                  style={{ display: 'inline-flex', width: 'fit-content' }}
                >
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <Typography>View full counterparty details</Typography>
                    <ArrowForwardIcon fontSize='medium' />
                  </Stack>
                </Link>
              )}
            </>
          }
        />
        <FormattedTabs
          defaultActiveKey={0}
          tabPanes={[
            {
              title: 'Transactions',
              key: 0,
              body: (
                <TransactionCounterpartiesTransactionsTable
                  originalTableData={originalTableData}
                  displayedTableData={displayedTableData}
                  setDisplayedTableData={setDisplayedTableData}
                  isLoading={isLoading}
                  onLoadTransactions={() => {
                    getCounterpartyTransactions()
                  }}
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                />
              ),
            },
            {
              title: 'Rules',
              key: 1,
              body: (
                <TransactionCounterpartiesListenersTable
                  originalTableData={cleanedCounterparties}
                  displayedTableData={displayCleanedCounterparties}
                  setDisplayedTableData={setDisplayCleanedCounterparties}
                  isLoading={cleanedCounterpartiesLoading}
                  counterparty={counterparty}
                  setOriginalTableData={setCleanedCounterparties}
                />
              ),
            },
          ]}
        />
      </Box>
    </Box>
  )
}

export default TransactionCounterpartyDetails
